import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Book, MagnifyingGlass, Bell, List, X, User, ChartBar, BookmarksSimple,
  Bookmark, PencilSimple, FileText, Heart, Users, Gear, Palette, Question,
  SignOut, Feather, ArrowRight, House, Crown
} from '@phosphor-icons/react';
import ThemeToggle from './ThemeToggle';
import { proxyImage } from '../lib/imageProxy';

const NAV_LINKS = [
  { to: '/', label: 'Home', testId: 'nav-home', icon: House },
  { 
    label: 'Library', 
    testId: 'nav-library', 
    icon: Book,
    dropdown: [
      { to: '/browse', label: 'Browse All' },
      { to: '/browse?category=Poem', label: 'Poems' },
      { to: '/browse?category=Story', label: 'Stories' },
      { to: '/browse?category=Short Story', label: 'Short Stories' },
      { to: '/browse?category=Novel', label: 'Novels' },
      { to: '/browse?category=Essay', label: 'Essays' },
      { to: '/browse?category=Ghazal', label: 'Ghazals' },
      { to: '/browse?category=Doha', label: 'Dohas' },
      { to: '/browse?category=Bhajan', label: 'Bhajans' },
      { to: '/browse?category=Shayari', label: 'Shayari' },
      { to: '/browse?category=Article', label: 'Articles' },
      { to: '/browse?category=Blog', label: 'Blogs' },
    ]
  },
  { 
    label: 'Authors', 
    testId: 'nav-authors', 
    icon: Feather,
    dropdown: [
      { to: '/authors/heritage', label: 'Literary Heritage' },
      { to: '/authors/community', label: 'Community Writers' },
    ]
  },
  { to: '/competitions', label: 'Competitions', testId: 'nav-competitions' },
  { to: '/page/about', label: 'About', testId: 'nav-about' },
  { to: '/page/contact', label: 'Contact', testId: 'nav-contact' },
  { to: '/page/help-center', label: 'Help', testId: 'nav-help' },
];

/** Shared header used on every page. */
export default function GlobalHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  const dropdownRef = useRef(null);
  const libraryDropdownRef = useRef(null);
  const authorsDropdownRef = useRef(null);

  // Close dropdowns on outside click / route change
  useEffect(() => {
    setMenuOpen(false);
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const onDoc = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (libraryDropdownRef.current && !libraryDropdownRef.current.contains(e.target) &&
          authorsDropdownRef.current && !authorsDropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/browse?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/');
  };

  const initials = (user?.name || user?.email || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border" data-testid="global-header">
      <div className="container mx-auto px-4 md:px-6 py-3 flex items-center gap-2 md:gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link" aria-label="Home">
          <Book size={26} weight="duotone" className="text-primary" />
          <span className="text-xl md:text-2xl hidden sm:inline" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
            Kavyalekh
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 ml-4">
          {NAV_LINKS.map((n) => {
            if (n.dropdown) {
              const isOpen = openDropdown === n.label;
              const dropdownRef = n.label === 'Library' ? libraryDropdownRef : authorsDropdownRef;
              
              return (
                <div key={n.label} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setOpenDropdown(isOpen ? null : n.label)}
                    data-testid={n.testId}
                    className="text-sm font-medium px-3 py-2 rounded-full hover:bg-muted transition-colors flex items-center gap-1"
                  >
                    {n.label}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg py-2 min-w-[200px] z-50 max-h-[400px] overflow-y-auto">
                      {n.dropdown.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className="block px-4 py-2 text-sm hover:bg-muted transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                className="text-sm font-medium px-3 py-2 rounded-full hover:bg-muted transition-colors"
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        {/* Search (desktop) */}
        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-sm ml-auto relative" role="search">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search works, authors, themes..."
            data-testid="global-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm bg-muted/50 border border-transparent focus:border-primary focus:bg-background rounded-full outline-none transition-colors"
          />
        </form>

        <div className="ml-auto md:ml-0 flex items-center gap-1 shrink-0">
          {/* Search icon (mobile) */}
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            data-testid="mobile-search-toggle"
            className="md:hidden w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center"
            aria-label="Search"
          >
            <MagnifyingGlass size={18} />
          </button>

          {/* Write */}
          <Link
            to={user ? "/editor" : "/login"}
            data-testid="nav-write"
            className="hidden sm:inline-flex items-center gap-1 px-3 h-9 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
          >
            <PencilSimple size={14} weight="bold" /> Write
          </Link>

          <ThemeToggle />

          {user ? (
            <>
              {/* Notifications */}
              <Link
                to="/dashboard#notifications"
                data-testid="nav-notifications"
                className="w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center relative"
                aria-label="Notifications"
              >
                <Bell size={18} />
              </Link>

              {/* Avatar dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setMenuOpen((s) => !s)}
                  data-testid="user-avatar-button"
                  className="w-9 h-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center overflow-hidden hover:border-primary transition-colors"
                  aria-label="User menu"
                >
                  {user.picture ? (
                    <img src={proxyImage(user.picture)} alt={user.name || 'User'} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs font-semibold text-primary">{initials}</span>
                  )}
                </button>

                {menuOpen && (
                  <UserDropdown
                    user={user}
                    onLogout={handleLogout}
                    onNavigate={() => setMenuOpen(false)}
                  />
                )}
              </div>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="signin-button"
              className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
            >
              Sign In <ArrowRight size={14} />
            </Link>
          )}

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={18} /> : <List size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile search bar */}
      {searchOpen && (
        <div className="md:hidden border-t border-border px-4 py-3">
          <form onSubmit={submitSearch} className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              autoFocus
              data-testid="mobile-search-input"
              className="w-full pl-9 pr-3 py-2 text-sm bg-muted/50 border border-transparent focus:border-primary focus:bg-background rounded-full outline-none"
            />
          </form>
        </div>
      )}

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="lg:hidden border-t border-border py-2" data-testid="mobile-nav">
          {NAV_LINKS.map((n) => {
            if (n.dropdown) {
              return (
                <div key={n.label}>
                  <div className="px-6 py-3 text-sm font-medium text-muted-foreground">
                    {n.label}
                  </div>
                  {n.dropdown.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="block pl-10 pr-6 py-2 text-sm hover:bg-muted"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              );
            }
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-${n.testId}`}
                className="block px-6 py-3 text-sm font-medium hover:bg-muted"
              >
                {n.label}
              </Link>
            );
          })}
          <Link
            to={user ? "/editor" : "/login"}
            data-testid="mobile-nav-write"
            className="sm:hidden block px-6 py-3 text-sm font-medium hover:bg-muted text-primary font-semibold"
          >
            ✍️ Write
          </Link>
        </nav>
      )}
    </header>
  );
}

function UserDropdown({ user, onLogout, onNavigate }) {
  const items = [
    { to: user?.user_id ? `/profile/${user.user_id}` : '/profile', icon: User, label: 'My Profile', testId: 'menu-profile' },
    { to: '/dashboard', icon: ChartBar, label: 'Dashboard', testId: 'menu-dashboard' },
    { to: '/collections', icon: BookmarksSimple, label: 'My Library', testId: 'menu-library' },
    { to: '/dashboard#continue', icon: Book, label: 'Continue Reading', testId: 'menu-continue' },
    { to: '/bookmarks', icon: Bookmark, label: 'Bookmarks', testId: 'menu-bookmarks' },
    { to: '/dashboard#drafts', icon: PencilSimple, label: 'My Drafts', testId: 'menu-drafts' },
    { to: '/dashboard#published', icon: FileText, label: 'My Published Works', testId: 'menu-published' },
    { to: '/dashboard#liked', icon: Heart, label: 'Liked Content', testId: 'menu-liked' },
    { to: '/dashboard#following', icon: Users, label: 'Following', testId: 'menu-following' },
    { to: '/dashboard#notifications', icon: Bell, label: 'Notifications', testId: 'menu-notifications' },
    { to: '/settings', icon: Gear, label: 'Account Settings', testId: 'menu-settings' },
  ];

  return (
    <div
      className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-xl shadow-lg overflow-hidden"
      data-testid="user-dropdown"
    >
      <div className="px-4 py-3 border-b border-border">
        <p className="text-sm font-semibold truncate" data-testid="menu-user-name">{user.name || 'Reader'}</p>
        <p className="text-xs text-muted-foreground truncate" data-testid="menu-user-email">{user.email}</p>
        {typeof user.points === 'number' && (
          <p className="text-xs text-primary mt-1">{user.points} points</p>
        )}
      </div>
      <div className="py-1 max-h-96 overflow-y-auto">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <Link
              key={it.to + it.label}
              to={it.to}
              onClick={onNavigate}
              data-testid={it.testId}
              className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-muted"
            >
              <Icon size={16} className="text-muted-foreground" />
              <span>{it.label}</span>
            </Link>
          );
        })}
        <div className="border-t border-border my-1"></div>
        <div className="flex items-center justify-between px-4 py-2 text-sm">
          <span className="flex items-center gap-3">
            <Palette size={16} className="text-muted-foreground" />
            Appearance
          </span>
          <ThemeToggle />
        </div>
        <a
          href="mailto:support@kavyalekh.com"
          data-testid="menu-help"
          className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-muted"
        >
          <Question size={16} className="text-muted-foreground" />
          <span>Help &amp; Support</span>
        </a>
        {user?.role === 'admin' && (
          <>
            <div className="border-t border-border my-1"></div>
            <Link
              to="/admin"
              onClick={onNavigate}
              data-testid="menu-admin"
              className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-muted text-primary font-medium"
            >
              <Crown size={16} className="text-primary" />
              <span>Admin Dashboard</span>
            </Link>
          </>
        )}
        <div className="border-t border-border my-1"></div>
        <button
          onClick={onLogout}
          data-testid="menu-logout"
          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
        >
          <SignOut size={16} />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}
